import React, { useEffect, useState } from "react";
import {
  CardContent,
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import LoadingSpinner from "../../../LoadingSpinner";
import {
  useGetAllWorkflowsQuery,
  useGetAllTeamsSelectedWorkflowIdQuery,
  useGetLatestStatusReportQuery,
  useLazyGetIssueTypeWiseTATReportDownloadQuery,
} from "../../../../Services/reports/tickets/ReportTickets";
import { toast } from "react-toastify";
import CsvDownloader from "react-csv-downloader";
import { AUTH_TOKEN } from "../../../../constants";

const getTodayDateString = () => new Date().toISOString().split("T")[0];

const isCurrentDateRange = (from: string, to: string) => {
  const today = getTodayDateString();
  return from === today && to === today;
};

type ReportMode = "live" | "historical" | null;

const optionsStatus: { label: string; value: string }[] = [
  { label: "Open", value: "active" },
  { label: "In Progress", value: "wip" },
  { label: "Closed", value: "closed" },
  { label: "Reopen", value: "reopen" },

];

const isDate = (value: any) => {
  if (typeof value !== "string") return false;
  const datePatterns = [
    /^\d{4}-\d{1,2}-\d{1,2}$/,
    /^\d{4}-\d{1,2}-\d{1,2} \d{1,2}:\d{1,2}(?::\d{1,2})?$/,
    /^\d{4}-\d{1,2}-\d{1,2}T\d{1,2}:\d{1,2}(?::\d{1,2})?/,
    /^\d{1,2}\/\d{1,2}\/\d{4}$/,
    /^\d{4}\/\d{1,2}\/\d{1,2}$/,
  ];
  return datePatterns.some((p) => p.test(value)) && !isNaN(Date.parse(value));
};

const formatDateTime = (value: any) => {
  if (!value) return "-";
  try {
    let normalizedValue = value;
    if (/^\d{4}-\d{1,2}-\d{1,2} \d{1,2}:\d{1,2}(?::\d{1,2})?$/.test(value)) {
      normalizedValue = value.replace(
        /(\d{4})-(\d{1,2})-(\d{1,2}) (\d{1,2}):(\d{1,2}):?(\d{0,2})/,
        (_: string, y: string, M: string, d: string, H: string, m: string, s: string) => {
          const pad = (n: string) => n.padStart(2, "0");
          const sec = s ? `:${pad(s)}` : "";
          return `${y}-${pad(M)}-${pad(d)} ${pad(H)}:${pad(m)}${sec}`;
        }
      );
    }
    const date = new Date(normalizedValue);
    if (isNaN(date.getTime())) return value;
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(String(value).trim())) {
      return `${day}/${month}/${year}`;
    }
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  } catch {
    return value;
  }
};

const LatestStatusReport = () => {
  const navigate = useNavigate();
  const { data: workflowsData, isLoading: workflowsLoading } = useGetAllWorkflowsQuery({});
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string | null>(null);
  const [params, setParams] = useState<any>(null);
  const [optionsTeams, setOptionsTeams] = useState<any[]>([]);
  const [optionsWorkflow, setOptionsWorkflow] = useState<any[]>([]);
  const [selectedTeams, setSelectedTeams] = useState<any>([]);
  const [selectedTeamsParam, setSelectedTeamsParam] = useState<string[]>([]);
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const [selectedStatus, setSelectedStatus] = useState<any[]>([]);
  const [dataRows, setDataRows] = useState<any[]>([]);
  const [columns, setColumns] = useState<any>([]);
  const [localLoading, setLocalLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 20,
    page: 0,
  });
  const [reportFetching, setReportFetching] = useState(false);
  const [reportMode, setReportMode] = useState<ReportMode>(null);

  const {
    data: liveLatestStatusReportData,
    isFetching: liveReportFetching,
    status: liveReportStatus,
  } = useGetLatestStatusReportQuery(params, {
    skip: !params || reportMode !== "live",
  });

  const [triggerLatestStatusDownload, { isFetching: downloadLoading }] =
    useLazyGetIssueTypeWiseTATReportDownloadQuery();
  const [totalRowsFromApi, setTotalRowsFromApi] = useState<number | null>(null);

  const reportingBaseUrl =
    process.env.REACT_APP_REPORTING_SERVER_URL || "https://reporting.bluewhirl.org";

  const { data: teamData, isLoading: teamLoading } =
    useGetAllTeamsSelectedWorkflowIdQuery(selectedWorkflowId, {
      skip: !selectedWorkflowId,
    });

  useEffect(() => {
    if (teamData && teamData.results?.length > 0) {
      const transformedOptions = teamData.results.map((team: any) => ({
        label: team.team_name,
        value: team.id,
      }));
      setOptionsTeams(transformedOptions);
    }
  }, [teamData]);

  useEffect(() => {
    if (workflowsData && workflowsData.results?.length > 0) {
      const transformedOptions = workflowsData.results.map((workflow: any) => ({
        label: workflow.workflow_name,
        value: workflow.id,
      }));
      setOptionsWorkflow(transformedOptions);
    }
  }, [workflowsData]);

  const processReportResults = (results: any[], rowOffset = 0) => {
    if (results.length === 0) {
      setColumns([]);
      setDataRows([]);
      return;
    }
    const keys = Object.keys(results[0]);
    const newColumns = keys.map((key: string) => ({
      field: key,
      headerName: key,
      align: "center",
      headerAlign: "center",
    }));
    const newRows = results.map((row: any, index: number) => {
      const processedRow: any = {};
      keys.forEach((key: string) => {
        const value = row[key];
        if (
          value === "" ||
          value === null ||
          value === "-" ||
          value === "--"
        ) {
          processedRow[key] = "-";
        } else if (isDate(value)) {
          processedRow[key] = formatDateTime(value);
        } else {
          processedRow[key] = value;
        }
      });
      return { id: rowOffset + index, ...processedRow };
    });
    setColumns(newColumns);
    setDataRows(newRows);
  };

  useEffect(() => {
    if (!params || reportMode !== "historical") {
      if (reportMode !== "historical") {
        setDataRows([]);
        setColumns([]);
        setTotalRowsFromApi(null);
      }
      return;
    }

    const controller = new AbortController();
    setLocalLoading(true);
    setReportFetching(true);

    const queryParams: Record<string, string | number | boolean> = {
      from_date: params.gte,
      to_date: params.lte,
      page: params.page ?? 1,
      page_size: params.page_size ?? 20,
      report_type: "latest_status_of_tickets",
      download: false,
      workflow_id: params.workflow_id,
      status: params.status,
    };
    if (params.team_ids != null && params.team_ids !== "") {
      queryParams.team_ids =
        typeof params.team_ids === "string"
          ? params.team_ids
          : Array.isArray(params.team_ids)
            ? params.team_ids.join(",")
            : String(params.team_ids);
    }

    axios
      .get(`${reportingBaseUrl}/ticket/sla-reports`, {
        params: queryParams,
        signal: controller.signal,
        headers: {
          Authorization: `Bearer ${localStorage.getItem(AUTH_TOKEN)}`,
        },
      })
      .then((res) => {
        const data = res?.data;
        const results = Array.isArray(data?.results)
          ? data.results
          : Array.isArray(data?.data)
            ? data.data
            : Array.isArray(data)
              ? data
              : [];
        const totalRows =
          data?.total_rows ?? data?.count ?? data?.total ?? results.length;
        setTotalRowsFromApi(
          typeof totalRows === "number" ? totalRows : results.length
        );

        const serverPage = data?.current_page ?? data?.page ?? params.page ?? 1;
        const serverPageSize = data?.page_size ?? params.page_size ?? 20;
        const apiPage = Number(serverPage);
        const pageSizeNum = Number(serverPageSize);
        setPaginationModel({
          page: Math.max(0, apiPage - 1),
          pageSize: pageSizeNum,
        });

        const rowOffset = (apiPage - 1) * pageSizeNum;
        processReportResults(results, rowOffset);
      })
      .catch((err) => {
        if (axios.isCancel(err)) return;
        toast.error(
          err?.response?.data?.message ||
            "Failed to fetch Latest Status report"
        );
        setColumns([]);
        setDataRows([]);
      })
      .finally(() => {
        setLocalLoading(false);
        setReportFetching(false);
      });

    return () => controller.abort();
  }, [params, reportingBaseUrl, reportMode]);

  useEffect(() => {
    if (reportMode !== "live") return;
    if (params) setLocalLoading(true);
  }, [params, reportMode]);

  useEffect(() => {
    if (reportMode !== "live") return;
    if (!Array.isArray(liveLatestStatusReportData)) {
      setDataRows([]);
      setColumns([]);
      setLocalLoading(false);
      return;
    }
    processReportResults(liveLatestStatusReportData);
    setLocalLoading(false);
  }, [liveLatestStatusReportData, reportMode]);

  useEffect(() => {
    const dropdownHeadings = document.querySelectorAll(".dropdown-heading");
    dropdownHeadings.forEach((heading: any) => {
      heading.style.height = "50px";
    });
  }, []);

  const handleTeamsChange = (event: any) => {
    const {
      target: { value },
    } = event;

    if (value.includes("all")) {
      setSelectedTeams(
        selectedTeams.length === optionsTeams.length
          ? []
          : optionsTeams.map((team) => team.value),
      );
    } else {
      // Convert the value to an array of strings
      setSelectedTeams(typeof value === "string" ? value.split(",") : value);
    }
  };

  useEffect(() => {
    setSelectedTeamsParam(selectedTeams);
  }, [selectedTeams]);

  const ITEM_HEIGHT = 60;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 150,
      },
    },
  };
  const isAllSelected = selectedTeams.length === optionsTeams.length;

  const handleWorkflowChange = (event: any) => {
    setSelectedWorkflowId(event.target.value);
  };

  const handleStatusChange = (event: any) => {
    const {
      target: { value },
    } = event;

    if (value.includes("all")) {
      setSelectedStatus(
        selectedStatus.length === optionsStatus.length
          ? []
          : optionsStatus.map((status) => status.value),
      );
    } else {
      // Convert the value to an array of strings
      setSelectedStatus(typeof value === "string" ? value.split(",") : value);
    }
  };

  const isAllStatusSelected = selectedStatus.length === optionsStatus.length;

  const handleDateFromChange = (event: any) => {
    setDateFrom(event.target.value);
  };

  const handleDateToChange = (event: any) => {
    setDateTo(event.target.value);
  };

  const handleSubmit = () => {
    if (!dateFrom) {
      toast.error('Please select "From Date"');
      return;
    }
    if (!dateTo) {
      toast.error('Please select "To Date"');
      return;
    }

    if (!selectedTeamsParam.length) {
      toast.error('Please select "Team"');
      return;
    }

    if (!selectedWorkflowId) {
      toast.error("Please select a workflow");
      return;
    }

    if (!selectedStatus.length) {
      toast.error("Please select a status");
      return;
    }
    const isLive = isCurrentDateRange(dateFrom, dateTo);
    const statusForApi = selectedStatus
      .map(
        (s) => optionsStatus.find((o) => o.value === s)?.value ?? String(s)
      )
      .join(",");

    const queryParams: any = {
      gte: dateFrom,
      lte: dateTo,
      workflow_id: selectedWorkflowId,
    };

    if (isLive) {
      if (selectedTeamsParam.length > 1) {
        queryParams.team_ids = selectedTeamsParam;
      } else if (selectedTeamsParam.length === 1) {
        queryParams.team_id = selectedTeamsParam[0];
      }
      queryParams.status_type =
        selectedStatus.length > 1 ? statusForApi : selectedStatus[0];
    } else {
      queryParams.page = 1;
      queryParams.page_size = paginationModel.pageSize;
      queryParams.status = statusForApi;
      if (selectedTeamsParam.length > 1) {
        queryParams.team_ids = selectedTeamsParam.join(",");
      } else if (selectedTeamsParam.length === 1) {
        queryParams.team_ids = String(selectedTeamsParam[0]);
      }
    }

    setReportMode(isLive ? "live" : "historical");
    setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
    setParams(queryParams);
  };

  const handlePaginationChange = (newPaginationModel: any) => {
    setPaginationModel(newPaginationModel);
    if (!params) return;
    if (reportMode === "live") return;
    setParams({
      ...params,
      page: newPaginationModel.page + 1,
      page_size: newPaginationModel.pageSize,
    });
  };

  const gridLoading =
    reportMode === "live" ? localLoading || liveReportFetching : localLoading;
  const showSpinner =
    workflowsLoading ||
    teamLoading ||
    (reportMode === "live"
      ? liveReportStatus === "pending"
      : reportFetching || localLoading);

  const handleDownload = async () => {
    if (!params) {
      toast.error("Please apply filters and submit before generating the report.");
      return;
    }

    const downloadPayload: Record<string, string | number> = {
      from_date: params.gte,
      to_date: params.lte,
      page: 1,
      page_size: totalRowsFromApi ?? 99999,
      report_type: "latest_status_of_tickets",
      workflow_id: params.workflow_id,
      status: params.status,
    };
    if (params.team_ids != null && params.team_ids !== "") {
      downloadPayload.team_ids =
        typeof params.team_ids === "string"
          ? params.team_ids
          : Array.isArray(params.team_ids)
            ? params.team_ids.join(",")
            : String(params.team_ids);
    }

    try {
      await triggerLatestStatusDownload(downloadPayload).unwrap();
      toast.success("Report generation in progress. Download from exported report section once ready");
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to queue report generation.");
    }
  };

  const resetFilters = () => {
    setSelectedTeams([]);
    setSelectedWorkflowId(null);
    setDateFrom("");
    setDateTo("");
    setParams(null);
    setReportMode(null);
    setSelectedStatus([]);
    setDataRows([]);
    setTotalRowsFromApi(null);
    setColumns([]);
    setPaginationModel({ page: 0, pageSize: 20 });
  };

  return (
    <div id="kt_app_content_container" className="app-container mx-5">
      <div className="card card-flush">
        {/* Card Header */}
        <div
          className="card-header align-items-center py-1 gap-2 gap-md-5"
          style={{ justifyContent: "flex-start" }}
        >
          {/* <img
            src="https://insurance.circleonecrm.com/assets/arrow_back.svg"
            alt="Back"
            style={{ marginBottom: "-2px", cursor: "pointer" }}
            className="ng-star-inserted"
            onClick={() => navigate("/reports/ticket/reports-list")}
          /> */}
          <button
            className="btn btn-link mb-2"
            onClick={() => {
              navigate("/reports/ticket/reports-list");
            }}
            style={{ marginRight: "-17px" }}
          >
            <i
              className="bi bi-arrow-90deg-left"
              style={{
                fontSize: "20px",
                color: "black",
                cursor: "pointer",
              }}
            ></i>
          </button>
          <span className="fs-3 text-gray-800 fw-bold ms-3">
            Ticket status summary
          </span>
        </div>

        {/* Toolbar */}
        <div
          className="card-toolbar flex-row-fluid justify-content-start gap-5 d-flex mb-10"
          style={{ marginLeft: "30px" }}
        >
          {/* Date Filters */}
          <div className="w-100 mw-150px">
            <TextField
              id="date-from"
              label="From"
              type="date"
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                inputProps: {
                  style: { textTransform: "uppercase" }, // Optional: uppercase input text
                  max: new Date().toISOString().split("T")[0],
                },
              }}
              fullWidth
              value={dateFrom}
              onChange={handleDateFromChange}
            />
          </div>
          <div className="w-100 mw-150px">
            <TextField
              id="date-to"
              label="To"
              type="date"
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                inputProps: {
                  style: { textTransform: "uppercase" }, // Optional: uppercase input text
                  max: new Date().toISOString().split("T")[0],
                },
              }}
              fullWidth
              value={dateTo}
              onChange={handleDateToChange}
            />
          </div>
          <div className="w-100 mw-150px">
            <FormControl fullWidth>
              <InputLabel id="workflow-select-label">
                Select Workflow
              </InputLabel>
              <Select
                labelId="workflow-select-label"
                id="workflow-select"
                value={selectedWorkflowId || ""}
                onChange={handleWorkflowChange}
                label="Select Workflow"
                MenuProps={MenuProps}
              >
                {optionsWorkflow.map((workflow) => (
                  <MenuItem key={workflow.value} value={workflow.value}>
                    {workflow.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div className="w-100 mw-150px">
            <FormControl fullWidth>
              <InputLabel id="demo-multiple-checkbox-label">
                Select Teams
              </InputLabel>
              <Select
                labelId="demo-multiple-checkbox-label"
                id="demo-multiple-checkbox"
                multiple
                value={selectedTeams}
                onChange={handleTeamsChange}
                input={<OutlinedInput label="Select Teams" />}
                renderValue={(selected) => {
                  if (isAllSelected) {
                    return <div>All</div>;
                  }
                  return (
                    <div>
                      {selected
                        .map((teamId) => {
                          const team = optionsTeams.find(
                            (t) => t.value === teamId,
                          );
                          return team ? team.label : null;
                        })
                        .join(", ")}
                    </div>
                  );
                }}
                MenuProps={MenuProps}
              >
                <MenuItem
                  value="all"
                  onClick={() =>
                    setSelectedTeams(
                      isAllSelected
                        ? []
                        : optionsTeams.map((team) => team.value),
                    )
                  }
                >
                  <Checkbox
                    checked={isAllSelected}
                    indeterminate={selectedTeams?.length > 0 && !isAllSelected}
                  />
                  <ListItemText primary="All" />
                </MenuItem>
                {optionsTeams.map((team) => (
                  <MenuItem key={team.value} value={team.value}>
                    <Checkbox
                      checked={selectedTeams.indexOf(team.value) > -1}
                    />
                    <ListItemText primary={team.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div className="w-100 mw-150px">
            <FormControl fullWidth>
              <InputLabel id="demo-multiple-checkbox-label">
                Select Status
              </InputLabel>
              <Select
                labelId="demo-multiple-checkbox-label"
                id="demo-multiple-checkbox"
                multiple
                value={selectedStatus}
                onChange={handleStatusChange}
                input={<OutlinedInput label="Select Status" />}
                renderValue={(selected) => {
                  if (isAllStatusSelected) {
                    return <div>All</div>;
                  }
                  return (
                    <div>
                      {selected
                        .map((statusValue) => {
                          const status = optionsStatus.find(
                            (s) => s.value === statusValue,
                          );
                          return status ? status.label : null;
                        })
                        .join(", ")}
                    </div>
                  );
                }}
                MenuProps={MenuProps}
              >
                <MenuItem
                  value="all"
                  onClick={() =>
                    setSelectedStatus(
                      isAllStatusSelected
                        ? []
                        : optionsStatus.map((status) => status.value),
                    )
                  }
                >
                  <Checkbox
                    checked={isAllStatusSelected}
                    indeterminate={
                      selectedStatus?.length > 0 && !isAllStatusSelected
                    }
                  />
                  <ListItemText primary="All" />
                </MenuItem>
                {optionsStatus.map((status) => (
                  <MenuItem key={status.value} value={status.value}>
                    <Checkbox
                      checked={selectedStatus.indexOf(status.value) > -1}
                    />
                    <ListItemText primary={status.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          <div className="ms-auto d-flex gap-3" style={{ marginRight: "20px" }}>
            <button className="btn btn-primary" onClick={handleSubmit}>
              Submit
            </button>
            {reportMode === "live" ? (
              <button
                className="btn btn-primary"
                disabled={!params || dataRows.length === 0}
              >
                <CsvDownloader
                  filename="Ticket Status Summary"
                  datas={dataRows.map((row) => {
                    const safeRow: any = {};
                    Object.keys(row).forEach((k) => {
                      let val = row[k];
                      if (typeof val === "string") {
                        val = val.replace(/"/g, '""');
                      }
                      safeRow[k] = val;
                    });
                    return safeRow;
                  })}
                  columns={columns.map((col: any) => ({
                    id: col.field,
                    displayName: col.headerName,
                  }))}
                  separator=","
                  wrapColumnChar={`"`}
                >
                  Download
                </CsvDownloader>
              </button>
            ) : (
              <button
                className="btn btn-primary"
                onClick={handleDownload}
                disabled={!params || downloadLoading}
              >
                {downloadLoading ? "Loading..." : "Download"}
              </button>
            )}
            <button className="btn btn-link" onClick={resetFilters}>
              <i className="bi bi-funnel fs-2 text-dark"></i>
            </button>
          </div>
        </div>

        {/* DataGrid */}
        <CardContent style={{ width: "100%" }}>
          {showSpinner ? (
            <LoadingSpinner />
          ) : (
            <DataGrid
              rows={dataRows}
              columns={columns}
              pagination
              {...(reportMode === "historical"
                ? {
                    paginationMode: "server" as const,
                    rowCount: totalRowsFromApi ?? 0,
                  }
                : {})}
              paginationModel={paginationModel}
              onPaginationModelChange={handlePaginationChange}
              loading={gridLoading}
              disableColumnMenu
              pageSizeOptions={[5, 10, 15, 20]}
              localeText={{
                noRowsLabel: "No rows found",
              }}
              sx={{
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "lightgray",
                },
                "& .MuiDataGrid-columnHeader": {
                  backgroundColor: "lightgray",
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontWeight: "bold",
                  overflow: "visible",
                  whiteSpace: "normal",
                  textOverflow: "initial",
                  lineHeight: "normal",
                  textAlign: "center",
                },
                "& .MuiDataGrid-cell": {
                  textAlign: "center",
                },
                // Target the pagination select and actions by stable component classes
                "& .MuiTablePagination-select, .MuiTablePagination-actions": {
                  marginBottom: "10px",
                },
                // Target the DataGrid overlay wrapper with its stable class
                "& .MuiDataGrid-overlayWrapper": {
                  height: "50px",
                },
              }}
            />
          )}
        </CardContent>
      </div>
    </div>
  );
};

export default LatestStatusReport;
